import { useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Printer } from 'lucide-react';
import {
  OpticalJobStatus,
  isMultifocalLensType,
  type OpticalJobCollection,
  type OpticalJobDetail,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useCurrency } from '@/hooks/useCurrency';
import { usePermissions } from '@/hooks/usePermissions';
import { apiErrorCode } from '@/services/apiClient';
import {
  useOpticalJobActionMutation,
  useOpticalLabsListQuery,
  type OpticalJobActionBody,
} from '../../api/opticalJobs.api';
import { NEXT_STEP } from '../../tracking/jobs';
import { trackingErrorMessage } from '../../tracking/errors';
import { JobStatusBadge } from '../JobStatusBadge';
import { PrescriptionOrigin } from '../PrescriptionOrigin';

interface JobNextStepProps {
  job: OpticalJobDetail;
  customerName: string | null;
  /** Null for a reader without sales.view: the handover is then left to the server to judge. */
  collection: OpticalJobCollection | null;
  /** The job's clinical prescription is known to be unusable now: sending is not offered. */
  clinicalUnusable: boolean;
  /** Unsaved preparation on the page: sending waits until it is saved or dropped. */
  unsavedChanges: boolean;
  onMoved: (message: string) => void;
  /** Someone else changed the job: read it again, never repeat the change. */
  onConflict: () => void;
  onCollect?: () => void;
  /** Named when the order belongs to another branch than the one in use: the pair is handed over there, never here. */
  otherBranchName: string | null;
}

/**
 * The one step forward a job allows now, for someone who may take it.
 *
 * Each step names the version on screen and is confirmed first; a refusal keeps the user here with the reason, and a job moved by someone else meanwhile is read again rather than moved twice. Sending waits for everything the lab needs. The handover says what it waits on when money is owed, and offers to collect it where the user may. There is never a step back.
 */
export function JobNextStep({
  job,
  customerName,
  collection,
  clinicalUnusable,
  unsavedChanges,
  onMoved,
  onConflict,
  onCollect,
  otherBranchName,
}: JobNextStepProps) {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { format } = useCurrency();
  const [open, setOpen] = useState(false);
  const [labId, setLabId] = useState('');
  const [labReference, setLabReference] = useState('');
  const [error, setError] = useState<string | null>(null);
  const labs = useOpticalLabsListQuery(false);
  const action = useOpticalJobActionMutation(job.id);
  // A second click lands before the button re-renders as busy: only the first may send.
  const inFlight = useRef(false);

  const next = NEXT_STEP[job.status];
  const legacy = job.origin === 'LEGACY';
  const permitted = Boolean(next && can(next.permission)) && !legacy;
  // A pair is handed over only at its order's branch; the server refuses it anywhere else.
  const handoverElsewhere = permitted && next?.action === 'deliver' ? otherBranchName : null;
  const allowed = permitted && !handoverElsewhere;

  // What the lab needs that the screen can already see is missing; the server checks everything again.
  const lab = labId || job.lab?.id || '';
  const labActive =
    Boolean(lab) &&
    (job.lab?.id === lab ? job.lab.isActive : (labs.data ?? []).some((entry) => entry.id === lab));
  const fittingMissing =
    (job.lenses.right &&
      isMultifocalLensType(job.lenses.right.lensType) &&
      !job.rightFittingHeightMm) ||
    (job.lenses.left && isMultifocalLensType(job.lenses.left.lensType) && !job.leftFittingHeightMm);
  const sendBlockers = [
    !job.prescription ? t('opticalOrders.job.needsPrescription') : null,
    clinicalUnusable ? t('opticalOrders.job.needsUsablePrescription') : null,
    fittingMissing ? t('opticalOrders.job.needsFitting') : null,
    unsavedChanges ? t('opticalOrders.job.needsSaving') : null,
  ].filter((blocker): blocker is string => blocker !== null);
  const handoverHeld =
    job.status === OpticalJobStatus.READY_FOR_COLLECTION &&
    collection !== null &&
    !collection.handoverCovered;

  const start = () => {
    setError(null);
    setLabId('');
    setLabReference('');
    setOpen(true);
  };

  const confirm = () => {
    if (!next || inFlight.current) return;
    inFlight.current = true;
    const body: OpticalJobActionBody = { version: job.version };
    if (next.action === 'send-to-lab') {
      if (!job.lab || !job.lab.isActive || labId) body.labId = lab;
      if (labReference.trim()) body.labReference = labReference.trim();
    }
    if (next.action === 'mark-in-lab' && !job.labReference && labReference.trim()) {
      body.labReference = labReference.trim();
    }
    action.mutate(
      { action: next.action, body },
      {
        onSettled: () => {
          inFlight.current = false;
        },
        onSuccess: (moved) => {
          setOpen(false);
          onMoved(
            t('opticalOrders.job.moved', { status: t(`sales.opticalJobs.status.${moved.status}`) }),
          );
        },
        onError: (caught) => {
          if (apiErrorCode(caught) === 'OPTICAL_JOB_VERSION_CONFLICT') {
            setOpen(false);
            onConflict();
            return;
          }
          setError(trackingErrorMessage(t, caught));
        },
      },
    );
  };

  const summary = (
    <dl className="grid gap-2 text-sm">
      <Row label={t('opticalOrders.job.jobLabel')}>
        <span className="font-mono" dir="ltr">
          {job.reference}
        </span>
      </Row>
      {customerName ? <Row label={t('opticalOrders.builder.customer')}>{customerName}</Row> : null}
      {next?.action === 'send-to-lab' ? (
        <>
          <Row label={t('opticalOrders.steps.prescription')}>
            <PrescriptionOrigin prescription={job.prescription} />
          </Row>
          <Row label={t('opticalOrders.steps.frame')}>
            <span dir="auto">
              {[job.frame?.productName, job.frame?.variantName].filter(Boolean).join(' — ') ||
                t('opticalOrders.tracking.frameNotRecorded')}
            </span>
          </Row>
          <Row label="OD">
            <span dir="auto">{job.lenses.right?.productName ?? '—'}</span>
            {job.rightFittingHeightMm ? (
              <span className="text-muted font-mono text-xs" dir="ltr">
                {' '}
                · {job.rightFittingHeightMm} mm
              </span>
            ) : null}
          </Row>
          <Row label="OS">
            <span dir="auto">{job.lenses.left?.productName ?? '—'}</span>
            {job.leftFittingHeightMm ? (
              <span className="text-muted font-mono text-xs" dir="ltr">
                {' '}
                · {job.leftFittingHeightMm} mm
              </span>
            ) : null}
          </Row>
        </>
      ) : null}
      {next?.action === 'deliver' && collection ? (
        <Row label={t('opticalOrders.job.balance')}>
          {collection.outstandingMinor === 0
            ? t('opticalOrders.tracking.paid')
            : `${format(collection.outstandingMinor)} · ${t('opticalOrders.tracking.onPlan')}`}
        </Row>
      ) : null}
    </dl>
  );

  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
        <div className="min-w-0 space-y-1">
          <p className="flex flex-wrap items-center gap-2">
            <span className="text-muted text-sm">{t('opticalOrders.job.now')}</span>
            <JobStatusBadge status={job.status} />
          </p>
          <p className="text-muted text-sm">{t(`opticalOrders.job.statusHint.${job.status}`)}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href={`/print/lab-ticket/${job.id}`}
            target="_blank"
            rel="noreferrer"
            className="text-ink border-line hover:bg-canvas inline-flex h-10 items-center justify-center gap-2 rounded-lg border bg-white px-4 text-sm font-medium"
          >
            <Printer className="size-4" aria-hidden />
            {t('opticalOrders.job.printTicket')}
          </a>
          {allowed && next ? (
            <Button
              onClick={start}
              disabled={(next.action === 'send-to-lab' && sendBlockers.length > 0) || handoverHeld}
            >
              {t(`opticalOrders.job.actions.${next.action}`)}
              <ArrowRight className="size-4 rtl:rotate-180" aria-hidden />
            </Button>
          ) : null}
        </div>
      </div>

      {legacy ? (
        <div className="px-5 pb-4">
          <Alert tone="info">
            {t('opticalOrders.job.legacyNotice')}{' '}
            <Link
              className="text-brand-700 font-medium underline"
              to={`/sales/orders/${job.orderId}`}
            >
              {t('opticalOrders.job.openOrderPage')}
            </Link>
          </Alert>
        </div>
      ) : null}
      {handoverElsewhere ? (
        <div className="px-5 pb-4">
          <Alert tone="info">
            {t('opticalOrders.job.handOverElsewhere', { branch: handoverElsewhere })}
          </Alert>
        </div>
      ) : null}
      {allowed && next?.action === 'send-to-lab' && sendBlockers.length > 0 ? (
        <div className="px-5 pb-4">
          <Alert tone="warning">
            <p className="font-medium">{t('opticalOrders.job.cannotSendYet')}</p>
            <ul className="mt-1 list-inside list-disc">
              {sendBlockers.map((blocker) => (
                <li key={blocker}>{blocker}</li>
              ))}
            </ul>
          </Alert>
        </div>
      ) : null}
      {handoverHeld && collection ? (
        <div className="space-y-3 px-5 pb-4">
          <Alert tone="warning">
            {t('opticalOrders.job.handoverHeld', { amount: format(collection.outstandingMinor) })}
          </Alert>
          {onCollect ? (
            <Button variant="secondary" onClick={onCollect}>
              {t('opticalOrders.collect.open')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {open && next ? (
        <Modal
          isOpen
          onClose={() => setOpen(false)}
          title={t(`opticalOrders.job.confirm.${next.action}.title`)}
          description={t(`opticalOrders.job.confirm.${next.action}.body`)}
          footer={
            <>
              <Button
                variant="secondary"
                onClick={() => setOpen(false)}
                disabled={action.isPending}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={confirm}
                isLoading={action.isPending}
                disabled={next.action === 'send-to-lab' && !labActive}
              >
                {t(`opticalOrders.job.actions.${next.action}`)}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            {error ? <Alert tone="error">{error}</Alert> : null}
            {summary}
            {next.action === 'send-to-lab' ? (
              <>
                <FormField label={t('opticalOrders.job.lab')} required>
                  {({ id, describedBy }) => (
                    <Select
                      id={id}
                      aria-describedby={describedBy}
                      value={lab && labActive ? lab : ''}
                      placeholder={t('opticalOrders.job.chooseLab')}
                      onChange={(event) => setLabId(event.target.value)}
                      options={(labs.data ?? []).map((entry) => ({
                        value: entry.id,
                        label: `${entry.name} · ${t(entry.isInHouse ? 'opticalOrders.labs.inHouse' : 'opticalOrders.labs.external')}`,
                      }))}
                    />
                  )}
                </FormField>
                {(labs.data ?? []).length === 0 ? (
                  <Alert tone="warning">{t('opticalOrders.job.noActiveLabs')}</Alert>
                ) : null}
              </>
            ) : null}
            {next.action === 'send-to-lab' ||
            (next.action === 'mark-in-lab' && !job.labReference) ? (
              <Input
                label={t('opticalOrders.job.labReferenceOptional')}
                dir="ltr"
                className="font-mono rtl:text-right"
                maxLength={64}
                value={labReference}
                onChange={(event) => setLabReference(event.target.value)}
                hint={t('opticalOrders.job.labReferenceHint')}
              />
            ) : null}
          </div>
        </Modal>
      ) : null}
    </Card>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap gap-x-3">
      <dt className="text-muted min-w-24">{label}</dt>
      <dd className="text-ink min-w-0">{children}</dd>
    </div>
  );
}
